import { EnvironmentState, EnvironmentApp, EnvironmentLayout, EnvironmentSupport, EnvironmentEndpoint, EnvironmentUser, EnvironmentSelection } from './environment.types';

// ─────────────────────────────────────────────────────────────────────────────
// localStorage keys — one bucket per environment slice
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEYS = {
    settings: 'app-settings',
    layout: 'app-layout',
    endpoints: 'app-endpoints',
    support: 'app-support',

    user: 'app-user',
    selection: 'user-selection'
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Storage helpers
// ─────────────────────────────────────────────────────────────────────────────

function loadFromStorage<T>(key: string): T {
    if (typeof localStorage === 'undefined') return {} as T;
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) as T : {} as T;
    } catch {
        return {} as T;
    }
}

function saveToStorage(key: string, value: unknown): void {
    if (typeof localStorage === 'undefined') return;
    try {
        if (value === null || value === undefined) {
            localStorage.removeItem(key);
        } else {
            localStorage.setItem(key, JSON.stringify(value));
        }
    } catch { /* storage quota exceeded — silently ignore */ }
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createEnvironment(production: boolean): EnvironmentState {
    const state: EnvironmentState = {
        production,
        app: loadFromStorage<EnvironmentApp>(STORAGE_KEYS.settings),
        layout: loadFromStorage<EnvironmentLayout>(STORAGE_KEYS.layout),
        endpoints: loadFromStorage<EnvironmentEndpoint>(STORAGE_KEYS.endpoints),
        support: loadFromStorage<EnvironmentSupport>(STORAGE_KEYS.support),
        user: loadFromStorage<EnvironmentUser>(STORAGE_KEYS.user),
        selection: loadFromStorage<EnvironmentSelection>(STORAGE_KEYS.selection),

        save: () => {
            saveToStorage(STORAGE_KEYS.settings, state.app);
            saveToStorage(STORAGE_KEYS.layout, state.layout);
            saveToStorage(STORAGE_KEYS.endpoints, state.endpoints);
            saveToStorage(STORAGE_KEYS.support, state.support);
            saveToStorage(STORAGE_KEYS.user, state.user);
            saveToStorage(STORAGE_KEYS.selection, state.selection);
        }
    };

    return new Proxy(state, {
        set(target, prop: string, value) {
            (target as any)[prop] = value;
            if (prop in STORAGE_KEYS) {
                saveToStorage(STORAGE_KEYS[prop as keyof typeof STORAGE_KEYS], value);
            }
            return true;
        }
    }) as EnvironmentState;
}

export async function initEnvironment(env: EnvironmentState): Promise<void> {
    if (env.endpoints.api) return;

    try {
        const res = await fetch('https://api.ashcorp.xyz/web/config');
        if (!res.ok) return;
        const config = await res.json();
        if (!config) return;

        env.endpoints = config.url as EnvironmentEndpoint;
        env.support = config.support as EnvironmentSupport;
        env.app = config.app as EnvironmentApp;
        env.layout = config.layout as EnvironmentLayout;

        env.user = config.user as EnvironmentUser;
        env.selection = config.selection as EnvironmentSelection;
    } catch { /* network error — app continues with empty defaults */ }
}